import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import Spinner from "./Spinner.jsx";
import InspectWindow from "./ItemInspectWindow.jsx";
import ItemHoverWindow from "./ItemHoverWindow.jsx";
import ItemsGrid from "./ItemsGrid.jsx";
import ItemsTable from "./ItemsTable.jsx";
import CoinAmount from "./CoinAmount.jsx";
import iqLogo from "../images/iq_logo.png";
import { fetchWikiImageForItem } from "../services/gw2wiki/gw2WikiService";
import {
  clearLabelFilters,
  filterItems,
  labelDefs,
  toggleTriStateLabelFilter,
} from "../utils/itemFilters";

const rarityColors = {
  Basic: "#fff",
  Fine: "#4fc3f7",
  Masterwork: "#43a047",
  Rare: "#fbc02d",
  Exotic: "#ff9800",
  Ascended: "#e040fb",
  Legendary: "#8e24aa",
};

function getBestItemLink(item) {
  const link = item.chat_link || item.name || "";
  if (item.count > 1 && link) {
    return `${link}(${item.count})`;
  }
  return link;
}

const MESSAGE_CHAR_LIMIT = 199;

function buildAggregatedItemLinks(items) {
  const aggregated = new Map();

  items.forEach((item) => {
    const currentCount = typeof item.count === "number" ? item.count : 0;
    if (currentCount <= 0) return;

    const baseLink = item.chat_link || item.name || "";
    if (!baseLink) return;

    if (aggregated.has(baseLink)) {
      const entry = aggregated.get(baseLink);
      entry.totalCount += currentCount;
    } else {
      aggregated.set(baseLink, { link: baseLink, totalCount: currentCount });
    }
  });

  return Array.from(aggregated.values())
    .map(({ link, totalCount }) => {
      if (totalCount > 1 && link) {
        return `${link}(${totalCount})`;
      }
      return link;
    })
    .filter(Boolean);
}

function buildChatLinkBatches(links) {
  const batches = [];
  let currentBatch = [];
  let currentLength = 0;

  links.forEach((link) => {
    if (!link) return;

    // If a single link is longer than the limit, it must live alone in its own batch.
    if (link.length > MESSAGE_CHAR_LIMIT) {
      if (currentBatch.length) {
        batches.push(currentBatch);
        currentBatch = [];
        currentLength = 0;
      }
      batches.push([link]);
      return;
    }

    const separatorLength = currentBatch.length > 0 ? 1 : 0; // "\n" between links

    if (currentLength + separatorLength + link.length <= MESSAGE_CHAR_LIMIT) {
      currentBatch.push(link);
      currentLength += separatorLength + link.length;
    } else {
      if (currentBatch.length) {
        batches.push(currentBatch);
      }
      currentBatch = [link];
      currentLength = link.length;
    }
  });

  if (currentBatch.length) {
    batches.push(currentBatch);
  }

  return batches;
}

function ItemsPanel({
  isLoggedIn,
  username,
  isIqMember,
  items = [],
  labelFilters,
  setLabelFilters,
  loadError,
  loading,
  showSpinner,
  assignmentMode,
  assignmentWinnerName,
  onRemoveItem,
  onDecrementItem,
  onIncrementItem,
  onAssignItem,
  onResetItems,
  onUpdateItems,
  onAddItemRequest,
  onNotify,
}) {
  const [hovered, setHovered] = useState(null);
  const [hoveredRect, setHoveredRect] = useState(null);
  const [chatLinkBatches, setChatLinkBatches] = useState(null);
  const [showBatchPopup, setShowBatchPopup] = useState(false);
  const [copyButtonRect, setCopyButtonRect] = useState(null);
  const [wikiThumbs, setWikiThumbs] = useState({}); // itemId -> wiki image URL
  const [wikiLoading, setWikiLoading] = useState({}); // itemId -> boolean loading state for wiki image
  const [inspectedItem, setInspectedItem] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [showGridNames, setShowGridNames] = useState(false);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  const handleLabelClick = (key) => {
    setLabelFilters((prev) => toggleTriStateLabelFilter(prev, key));
  };

  const handleClearFilters = () => {
    setLabelFilters(clearLabelFilters());
  };

  const handleRemove = (id) => onRemoveItem?.(id);

  const handleDecrement = (id) => {
    onDecrementItem?.(id);
  };

  const handleIncrement = (id) => {
    onIncrementItem?.(id);
  };

  // Filtering
  const filteredItems = filterItems(items, {
    labelFilters,
    searchTerm: deferredSearch,
  });

  const { filteredBuyTotal, filteredSellTotal } = useMemo(() => {
    return filteredItems.reduce(
      (acc, item) => {
        const count = Number.isFinite(Number(item?.count)) ? Number(item.count) : 0;
        const buyUnit = Number.isFinite(Number(item?.tp_buy_price)) ? Number(item.tp_buy_price) : 0;
        const sellUnit = Number.isFinite(Number(item?.tp_sell_price)) ? Number(item.tp_sell_price) : 0;

        acc.filteredBuyTotal += count * buyUnit;
        acc.filteredSellTotal += count * sellUnit;
        return acc;
      },
      { filteredBuyTotal: 0, filteredSellTotal: 0 }
    );
  }, [filteredItems]);

  const hasActiveFilters = Object.values(labelFilters).some((v) => v !== 0);

  const handleCopyLinks = (event) => {
    const links = buildAggregatedItemLinks(filteredItems);

    if (!links.length) {
      onNotify?.("No chat links to copy!", "error");
      return;
    }

    const fullMessage = links.join("\n");

    if (fullMessage.length <= MESSAGE_CHAR_LIMIT) {
      navigator.clipboard.writeText(fullMessage);
      onNotify?.("All chat links copied!", "success");
      return;
    }

    const batches = buildChatLinkBatches(links);

    if (batches.length === 1) {
      navigator.clipboard.writeText(batches[0].join("\n"));
      onNotify?.("Chat links copied (single long batch)!", "success");
      return;
    }

    const rect = event?.currentTarget?.getBoundingClientRect() || null;
    setCopyButtonRect(rect);
    setChatLinkBatches(batches);
    setShowBatchPopup(true);
  };

  const handleCopyBatch = (index) => {
    if (!chatLinkBatches || !chatLinkBatches[index]) return;
    const text = chatLinkBatches[index].join("\n");
    navigator.clipboard.writeText(text);
    onNotify?.(`Batch ${index + 1} copied!`, "success");
    setShowBatchPopup(false);
    setChatLinkBatches(null);
  };

  const handleCloseBatchPopup = () => {
    setShowBatchPopup(false);
    setChatLinkBatches(null);
  };

  const handleReset = () => {
    onResetItems?.();
    onNotify?.("Items reset!", "success");
  };

  const handleUpdate = async () => {
    await onUpdateItems?.();
    onNotify?.("Items updated from server!", "success");
  };

  const handleAddItem = () => {
    onAddItemRequest?.();
  };

  const hoveredItem = filteredItems.find((i) => i.id === hovered) || null;
  const hoveredWikiSrc = hoveredItem ? wikiThumbs[hoveredItem.id] : null;
  const hoveredImageSrc = hoveredItem
    ? (hoveredItem.image_url && String(hoveredItem.image_url).trim()) ||
      (hoveredWikiSrc && String(hoveredWikiSrc).trim()) ||
      null
    : null;
  const inspectedWikiSrc = inspectedItem ? wikiThumbs[inspectedItem.id] : null;
  const inspectedImageSrc = inspectedItem
    ? (inspectedItem.image_url && String(inspectedItem.image_url).trim()) ||
      (inspectedWikiSrc && String(inspectedWikiSrc).trim()) ||
      null
    : null;

  const inspectedIndex =
    inspectedItem && filteredItems.length
      ? filteredItems.findIndex((i) => i.id === inspectedItem.id)
      : -1;

  // With circular navigation, prev/next are available whenever we
  // have an inspected item and more than one item in the filtered list
  const hasPrevInspected =
    inspectedItem && filteredItems.length > 1 && inspectedIndex !== -1;
  const hasNextInspected = hasPrevInspected;

  const handleInspectPrev = () => {
    if (!inspectedItem) return;
    const idx = filteredItems.findIndex((i) => i.id === inspectedItem.id);
    if (idx === -1 || filteredItems.length === 0) return;
    const prevIndex = idx === 0 ? filteredItems.length - 1 : idx - 1;
    setInspectedItem(filteredItems[prevIndex]);
  };

  const handleInspectNext = () => {
    if (!inspectedItem) return;
    const idx = filteredItems.findIndex((i) => i.id === inspectedItem.id);
    if (idx === -1 || filteredItems.length === 0) return;
    const nextIndex = idx === filteredItems.length - 1 ? 0 : idx + 1;
    setInspectedItem(filteredItems[nextIndex]);
  };

  const handleInspectCopyLink = () => {
    if (!inspectedItem) return;
    const link = getBestItemLink(inspectedItem);
    if (!link) {
      onNotify?.("No chat link to copy for this item!", "error");
    } else {
      navigator.clipboard.writeText(link);
      onNotify?.("Chat link copied to clipboard!", "success");
    }
  };

  // Fetch GW2 wiki image for hovered or inspected item via gw2WikiService
  useEffect(() => {
    const wikiTarget = hoveredItem || inspectedItem;
    if (!wikiTarget || !wikiTarget.name) return;
    // If backend already provided a wiki image URL, no need to fetch
    if (wikiTarget.image_url) return;
    if (wikiThumbs[wikiTarget.id]) return;

    let cancelled = false;

    const targetId = wikiTarget.id;

    // Mark this item as loading from the wiki while we fetch
    setWikiLoading((prev) => ({ ...prev, [targetId]: true }));

    (async () => {
      try {
        const src = await fetchWikiImageForItem(wikiTarget);
        if (cancelled) return;
        if (src) {
          setWikiThumbs((prev) => ({ ...prev, [wikiTarget.id]: src }));
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("Unable to load wiki image for item.", error);
        }
      } finally {
        if (!cancelled) {
          setWikiLoading((prev) => ({ ...prev, [targetId]: false }));
        }
      }
    })();

    return () => {
      cancelled = true;
      setWikiLoading((prev) => ({ ...prev, [targetId]: false }));
    };
  }, [hoveredItem, inspectedItem, wikiThumbs]);

  // Handler for mouse enter to set hovered and rect
  const handleItemMouseEnter = (id, event) => {
    setHovered(id);
    if (event?.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      setHoveredRect(rect);
    }
  };

  // Handler for mouse leave
  const handleItemMouseLeave = () => {
    setHovered(null);
    setHoveredRect(null);
  };

  return (
    <>
      <div className="items-panel" style={{ position: "relative", zIndex: 1002 }}>
        {assignmentMode && (
          <div style={{ marginBottom: 12, color: "#9be7ff", fontSize: 13, fontWeight: 700 }}>
            Assign mode: click an item below to give it to {assignmentWinnerName}.
          </div>
        )}
        <h2>
          {isIqMember && (
            <img
              src={iqLogo}
              alt="IQ guild logo"
              style={{
                height: 26,
                width: "auto",
                marginRight: 8,
                verticalAlign: "middle",
              }}
            />
          )}
          Items
        </h2>
        <input
          type="text"
          className="login-input"
          placeholder="Search item name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ marginBottom: 12 }}
        />

        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#d8f5ff", fontSize: 14 }}>
            <input
              type="radio"
              name="items-view-mode"
              checked={viewMode === "grid"}
              onChange={() => setViewMode("grid")}
            />
            Grid
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#d8f5ff", fontSize: 14 }}>
            <input
              type="radio"
              name="items-view-mode"
              checked={viewMode === "table"}
              onChange={() => setViewMode("table")}
            />
            Table
          </label>
          {viewMode === "grid" && (
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#d8f5ff", fontSize: 14 }}>
              <input
                type="checkbox"
                checked={showGridNames}
                onChange={(event) => setShowGridNames(event.target.checked)}
              />
              Show names
            </label>
          )}
        </div>
        {/* Label filter bar */}
        <div style={{ display: "flex", flexWrap: "wrap", columnGap: 18, rowGap: 10, marginBottom: 18, alignItems: "center" }}>
          {labelDefs.map(({ key }) => {
            const state = labelFilters[key];
            let bg = "#f2f2f2", color = "#222", border = "1.5px solid #ddd";
            if (state === 1)  { bg = "#43a047"; color = "#fff"; border = "1.5px solid #43a047"; }
            if (state === -1) { bg = "#e74c3c"; color = "#fff"; border = "1.5px solid #e74c3c"; }
            return (
              <button
                key={key}
                onClick={() => handleLabelClick(key)}
                style={{ position: "relative", display: "inline-flex", alignItems: "center", background: bg, color, border, borderRadius: 22, padding: "6px 18px", fontWeight: 600, fontSize: 16, cursor: "pointer", transition: "all 0.15s" }}
              >
                {state !== 0 && <span style={{ position: "absolute", left: 8, fontWeight: "bold" }}>{state === 1 ? "✓" : "✕"}</span>}
                <span style={{ paddingLeft: state !== 0 ? 16 : 0 }}>{key}</span>
              </button>
            );
          })}
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="names-btn secondary"
              style={{ marginLeft: 4 }}
            >
              Clear
            </button>
          )}
        </div>

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 16,
            marginBottom: 14,
            padding: "8px 12px",
            borderRadius: 10,
            border: "1px solid rgba(144, 224, 239, 0.28)",
            background: "rgba(18, 43, 61, 0.4)",
            color: "#d9f2ff",
            fontSize: 13,
          }}
        >
          <span style={{ color: "#9be7ff", fontWeight: 700 }}>Filtered total</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Buy <CoinAmount value={filteredBuyTotal} compact color="#d9f2ff" />
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Sell <CoinAmount value={filteredSellTotal} compact color="#d9f2ff" />
          </span>
        </div>

        {loadError && (
          <div
            style={{
              marginTop: 4,
              marginBottom: 4,
              textAlign: "center",
              fontSize: 20,
              color: "rgba(180, 220, 255, 0.8)",
            }}
          >
            {loadError === "missing_api_key" ? (
              <>
                Set an API key on your{" "}
                <Link
                  to={username ? `/users/${username}` : "/"}
                  style={{ color: "#90e0ef" }}
                >
                  user page
                </Link>{" "}
                to see Guild stash
              </>
            ) : (
              loadError
            )}
          </div>
        )}

        {loading && loadError !== "missing_api_key" && showSpinner && (
          <>
            <Spinner />
            <p className="login-help-text" style={{ marginTop: 8, textAlign: "center" }}>
              Loading items from stash
            </p>
          </>
        )}

        {!loading && viewMode === "grid" && (
          <ItemsGrid
            items={filteredItems}
            selectedItemId={inspectedItem?.id ?? null}
            hoveredItemId={hovered}
            renderMode={showGridNames ? "detailed" : "icons"}
            assignmentMode={assignmentMode}
            showControls
            onItemMouseEnter={(item, event) => handleItemMouseEnter(item.id, event)}
            onItemMouseLeave={handleItemMouseLeave}
            onItemClick={(item) => {
              if (assignmentMode) {
                if ((item?.count ?? 0) > 0) {
                  onAssignItem?.(item);
                }
                return;
              }

              setInspectedItem(item);
            }}
            onDecrement={handleDecrement}
            onIncrement={handleIncrement}
            onRemove={handleRemove}
          />
        )}

        {!loading && viewMode === "table" && (
          <ItemsTable
            items={filteredItems}
            assignmentMode={assignmentMode}
            onRowClick={(item) => {
              if (assignmentMode) {
                if ((item?.count ?? 0) > 0) {
                  onAssignItem?.(item);
                }
                return;
              }

              setInspectedItem(item);
            }}
          />
        )}
        {/* Inspect modal for a single item (uses DTO image_url, wiki image, or icon) */}
        <InspectWindow
          visible={!!inspectedItem}
          title={inspectedItem ? inspectedItem.name : ""}
          itemIcon={inspectedItem ? inspectedItem.icon : null}
          itemCount={inspectedItem ? inspectedItem.count : 0}
          itemType={inspectedItem ? inspectedItem.type : ""}
          imageSrc={inspectedImageSrc}
          isImageLoading={
            !!(
              inspectedItem &&
              wikiLoading[inspectedItem.id] &&
              !inspectedImageSrc
            )
          }
          buyPrice={inspectedItem ? inspectedItem.tp_buy_price : null}
          sellPrice={inspectedItem ? inspectedItem.tp_sell_price : null}
          accentColor={
            inspectedItem ? rarityColors[inspectedItem.rarity] || "#234" : "#234"
          }
          fallbackText="No preview image available for this item."
          onClose={() => setInspectedItem(null)}
          onCopy={inspectedItem ? handleInspectCopyLink : undefined}
          onPrev={hasPrevInspected ? handleInspectPrev : undefined}
          onNext={hasNextInspected ? handleInspectNext : undefined}
        />

        <ItemHoverWindow
          visible={viewMode === "grid" && !!hoveredItem && !!hoveredRect}
          rect={hoveredRect}
          item={hoveredItem}
          imageSrc={hoveredImageSrc}
          isImageLoading={hoveredItem ? !!wikiLoading[hoveredItem.id] : false}
          accentColor={hoveredItem ? rarityColors[hoveredItem.rarity] || "#234" : "#234"}
          buyPrice={hoveredItem ? hoveredItem.tp_buy_price : null}
          sellPrice={hoveredItem ? hoveredItem.tp_sell_price : null}
        />

        {showBatchPopup && chatLinkBatches && copyButtonRect &&
          createPortal(
            <div
              style={{
                position: "fixed",
                left: copyButtonRect.left + copyButtonRect.width / 2 + 80,
                top: copyButtonRect.top,
                transform: "translate(-50%, -110%)",
                background: "#222",
                color: "#fff",
                padding: "12px 16px",
                borderRadius: 8,
                boxShadow: "0 2px 8px #0008",
                zIndex: 10001,
                minWidth: 260,
              }}
            >
              <div style={{ marginBottom: 8, fontWeight: "bold", fontSize: 14 }}>
                Chat link message too long, breaking it up into batches
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                {chatLinkBatches.map((batch, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleCopyBatch(idx)}
                    className="names-btn primary"
                    style={{ fontSize: 14, padding: "6px 10px" }}
                  >
                    Copy batch {idx + 1}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={handleCloseBatchPopup}
                  className="names-btn secondary"
                  style={{ fontSize: 12, padding: "4px 8px" }}
                >
                  Close
                </button>
              </div>
            </div>,
            document.body
          )}
      </div>

      {/* Actions - now outside items-panel, keep above overlay */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
          marginTop: 12,
          marginLeft: 0,
          width: "100%",
          position: "relative",
          zIndex: 1002,
        }}
      >
        <button
          onClick={handleCopyLinks}
          className="names-btn primary"
          style={{ fontSize: 14, padding: "10px 12px", flex: "1 1 calc(50% - 8px)", minWidth: 130 }}
        >
          Copy Chat Links
        </button>
        <button
          onClick={handleReset}
          className="names-btn secondary"
          style={{ fontSize: 14, padding: "10px 12px", flex: "1 1 calc(50% - 8px)", minWidth: 130 }}
        >
          Reset
        </button>
        <button
          onClick={handleAddItem}
          className="names-btn primary"
          style={{ fontSize: 14, padding: "10px 12px", flex: "1 1 calc(50% - 8px)", minWidth: 130 }}
        >
          Add item
        </button>
        {isLoggedIn && (
          <button
            onClick={handleUpdate}
            className="names-btn primary"
            style={{ fontSize: 14, padding: "10px 12px", flex: "1 1 calc(50% - 8px)", minWidth: 130 }}
          >
            Update
          </button>
        )}
      </div>
    </>
  );
}

export default ItemsPanel;
